import React from 'react';
import { useButton, useFocusRing, mergeProps } from 'react-aria';
import { useRef, useState } from 'react';
import {DragIndicator as DragIndicatorIcon} from "@mui/icons-material";

// Type definitions
interface DragState {
    isDragging: boolean;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
}

interface CardProps {
    /** The content to display inside the card */
    children?: React.ReactNode;
    /** The title text for the card */
    title?: string;
    /** The description text for the card */
    description?: string;
    /** Image URL to display at the top of the card */
    image?: string;
    /** Callback function when the card is pressed (makes card interactive) */
    onPress?: () => void;
    /** URL to navigate to when card is clicked (renders as link) */
    href?: string;
    /** Whether the card is disabled */
    isDisabled?: boolean;
    /** Visual variant of the card */
    variant?: 'default' | 'elevated' | 'outlined' | 'ghost';
    /** Additional CSS classes to apply */
    className?: string;
    /** Optional aria-label for accessibility */
    'aria-label'?: string;
    /** Optional aria-describedby for accessibility */
    'aria-describedby'?: string;
    /** Whether the card is draggable */
    isDraggable?: boolean;
    /** Callback when drag starts */
    onDragStart?: (e: React.DragEvent) => void;
    /** Callback when drag ends */
    onDragEnd?: (e: React.DragEvent) => void;
    /** Callback during drag */
    onDrag?: (e: React.DragEvent) => void;
    /** Data to transfer during drag */
    dragData?: any;
}

export const Card: React.FC<CardProps> = ({
                                              children,
                                              title,
                                              description,
                                              image,
                                              onPress,
                                              href,
                                              isDisabled = false,
                                              variant = 'default',
                                              className = '',
                                              'aria-label': ariaLabel,
                                              'aria-describedby': ariaDescribedBy,
                                              isDraggable = false,
                                              onDragStart,
                                              onDragEnd,
                                              onDrag,
                                              dragData
                                          }) => {

    const anchorRef = useRef<HTMLAnchorElement>(null);
    const divRef = useRef<HTMLDivElement>(null);

    const [dragState, setDragState] = useState<DragState>({
        isDragging: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0
    });

    const isInteractive = onPress || href;

    // Use the appropriate ref based on element type
    const activeRef = href ? anchorRef :  divRef;

    // Focus ring for keyboard navigation
    const { focusProps, isFocusVisible } = useFocusRing();

    // Drag event handlers
    const handleDragStart = (e: React.DragEvent) => {
        if (!isDraggable || isDisabled) return;

        setDragState(prev => ({
            ...prev,
            isDragging: true,
            startX: e.clientX,
            startY: e.clientY
        }));

        // Set drag data
        if (dragData) {
            e.dataTransfer.setData('application/json', JSON.stringify(dragData));
        }

        // Set drag effect
        e.dataTransfer.effectAllowed = 'move';

        // Call external onDragStart handler
        onDragStart?.(e);
    };

    const handleDrag = (e: React.DragEvent) => {
        if (!isDraggable || isDisabled) return;

        setDragState(prev => ({
            ...prev,
            currentX: e.clientX,
            currentY: e.clientY
        }));

        onDrag?.(e);
    };

    const handleDragEnd = (e: React.DragEvent) => {
        if (!isDraggable || isDisabled) return;

        setDragState(prev => ({
            ...prev,
            isDragging: false,
            currentX: 0,
            currentY: 0
        }));

        onDragEnd?.(e);
    };

    // Prevent default drag behavior on non-draggable cards
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
    };

    // Variant styles
    const variants = {
        default: 'bg-white border-gray-200 shadow-sm hover:shadow-md',
        elevated: 'bg-white border-gray-200 shadow-md hover:shadow-lg',
        outlined: 'bg-white border-gray-300 hover:border-gray-400',
        ghost: 'bg-transparent border-transparent hover:bg-gray-50'
    };

    // Interactive styles
    const interactiveStyles = isInteractive && !isDraggable
        ? 'cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
        : '';

    // Draggable styles
    const draggableStyles = isDraggable && !isDisabled
        ? 'cursor-grab active:cursor-grabbing select-none touch-none'
        : '';

    // Dragging styles
    const draggingStyles = dragState.isDragging
        ? 'opacity-50 scale-105 rotate-1 shadow-2xl z-50 transition-all duration-200'
        : '';

    // Disabled styles
    const disabledStyles = isDisabled
        ? 'opacity-50 cursor-not-allowed hover:scale-100 hover:shadow-sm'
        : '';

    // Focus visible styles
    const focusStyles = isFocusVisible ? 'ring-2 ring-blue-500 ring-offset-2' : '';

    const cardClasses = `${variants[variant]} ${interactiveStyles} ${draggableStyles} ${draggingStyles} ${disabledStyles} ${focusStyles} ${className}`;

    const cardContent = (
        <>
            {/*{isDraggable && !isDisabled && (*/}
            {/*    <DragIndicatorIcon sx={{color: 'text.secondary', fontSize: 18}}/>*/}
            {/*)}*/}
            {image && (
                <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                    <img
                        src={image}
                        alt=""
                        className="h-full w-full object-cover"
                        draggable={false} // Prevent image from being dragged separately
                    />
                </div>
            )}
            <div className="">
                {title && (
                    <h3 className="text-lg font-medium text-gray-900 pt-6 pl-6 pr-6 mb-2">
                        {title}
                    </h3>
                )}
                {description && (
                    <p className="text-gray-600 pl-6 pr-6 mb-4">
                        {description}
                    </p>
                )}
                {children}
            </div>
            {/*{isDraggable && !isDisabled && (*/}
            {/*    <div className="absolute top-2 right-2 text-gray-400">*/}
            {/*        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">*/}
            {/*            <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>*/}
            {/*        </svg>*/}
            {/*    </div>*/}
            {/*)}*/}
        </>
    );

    // Render as static card
    return (
        <div
            ref={divRef}
            className={`relative card-container ${cardClasses}`}
            draggable={isDraggable && !isDisabled}
            onDragStart={handleDragStart}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={handleDrop}>

            {cardContent}
        </div>
    );
};

// // Demo component with example usage
// const CardDemo: React.FC = () => {
//     const handleCardPress = (cardName: string) => {
//         alert(`${cardName} card pressed!`);
//     };
//
//     return (
//         <div className="p-8 bg-gray-50 min-h-screen">
//             <h1 className="text-3xl font-bold text-gray-900 mb-8">Card Component Examples</h1>
//
//             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//                 {/* Basic static card */}
//                 <Card
//                     title="Static Card"
//                     description="This is a basic card without any interaction."
//                     variant="default"
//                 >
//                     <div className="text-sm text-gray-500">
//                         Additional content can go here
//                     </div>
//                 </Card>
//
//                 {/* Interactive card with onPress */}
//                 <Card
//                     title="Interactive Card"
//                     description="Click me to see the interaction!"
//                     variant="elevated"
//                     onPress={() => handleCardPress('Interactive')}
//                 >
//                     <div className="flex items-center text-blue-600 text-sm font-medium">
//                         Click to interact →
//                     </div>
//                 </Card>
//
//                 {/* Card with image */}
//                 <Card
//                     title="Image Card"
//                     description="This card includes an image at the top."
//                     image="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop"
//                     variant="outlined"
//                     onPress={() => handleCardPress('Image')}
//                 />
//
//                 {/* Link card */}
//                 <Card
//                     title="Link Card"
//                     description="This card acts as a link to another page."
//                     href="#"
//                     variant="ghost"
//                 >
//                     <div className="text-sm text-purple-600 font-medium">
//                         External link →
//                     </div>
//                 </Card>
//
//                 {/* Disabled card */}
//                 <Card
//                     title="Disabled Card"
//                     description="This card is disabled and cannot be interacted with."
//                     variant="default"
//                     onPress={() => handleCardPress('Disabled')}
//                     isDisabled={true}
//                 >
//                     <div className="text-sm text-gray-400">
//                         This card is disabled
//                     </div>
//                 </Card>
//
//                 {/* Custom content card */}
//                 <Card
//                     variant="elevated"
//                     onPress={() => handleCardPress('Custom')}
//                 >
//                     <div className="text-center">
//                         <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
//                             <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
//                             </svg>
//                         </div>
//                         <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Layout</h3>
//                         <p className="text-gray-600 text-sm">
//                             Completely custom card content with icon and centered layout.
//                         </p>
//                     </div>
//                 </Card>
//             </div>
//
//             <div className="mt-12 p-6 bg-white rounded-lg border border-gray-200">
//                 <h2 className="text-xl font-semibold text-gray-900 mb-4">Accessibility Features</h2>
//                 <ul className="space-y-2 text-gray-600">
//                     <li>• Keyboard navigation support (Tab to focus, Enter/Space to activate)</li>
//                     <li>• Screen reader compatible with proper ARIA attributes</li>
//                     <li>• Focus management with visible focus indicators</li>
//                     <li>• Semantic HTML structure (button/link elements)</li>
//                     <li>• Disabled state handling</li>
//                     <li>• Proper color contrast ratios</li>
//                 </ul>
//             </div>
//         </div>
//     );
// };
//
// export default CardDemo;